import React, { useState, useEffect, useRef } from 'react';
import type {
  SendMessageSuccessResponse,
  RateLimitErrorResponse,
  DeliveryErrorResponse,
  ServiceUnavailableErrorResponse,
} from '../types/api';
// Import images
import meanImage from '../assets/images/mean.png';
import niceImage from '../assets/images/nice.png';

type ApiResponse =
  | SendMessageSuccessResponse
  | RateLimitErrorResponse
  | DeliveryErrorResponse
  | ServiceUnavailableErrorResponse;

// Message type definition
type PredefinedImageName = 'mean' | 'nice';

interface MessageConfig {
  text?: string;  // Optional text message
  image?: PredefinedImageName;  // Optional image name
}

// List of messages to randomly select from
// Each message can have text, image, or both
const MESSAGES: MessageConfig[] = [
  { text: "Dudley, BE NICE! 🩵" },
  { text: "Pretend you slept. Be nice. 💚" },
  { text: "Leave your leering at the club. 👀 Be nice!" },
  { text: "Hey Dudley, be nice today! 😇" },
  { text: "Remember to be nice, Dudley! 🌟" },
  { text: "Dudley. Nice. Today. Try it. ✨" },
  { text: "Be nice, Dudley! You got this! 💖" },
  { text: "Push th' little daisies and make 'em come up! ...and be nice! 🌼" },
  { text: "Hey Dudley, let's be nice today! 🧡" },
  { text: "¡Pórtate bien! 🩷" },
  { text: "Be nice, Dudley! 🩵"  },
  { text: "Be nice! Don't fire anyone! 😎"  },
  { text: "Someone around you may have debilitating anxiety. Be nice. 🤍"  },
  { text: "Reminder: Be Nice! 🤍"  }, 
  { text: "🤍💚💜💛 BE NICE!!! 🧡🩶❤️💙"  },
  { text: "Time to find some niceness! 🕵️🤎"  }
];

const App: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageWidth, setImageWidth] = useState<number | null>(null);

  // Preload the nice image so it's ready immediately when needed
  useEffect(() => {
    const img = new Image();
    img.src = niceImage;
  }, []);

  // Measure image width when it loads
  useEffect(() => {
    const updateImageWidth = () => {
      if (imageRef.current) {
        setImageWidth(imageRef.current.offsetWidth);
      }
    };

    if (imageRef.current) {
      if (imageRef.current.complete) {
        updateImageWidth();
      } else {
        imageRef.current.addEventListener('load', updateImageWidth);
        return () => {
          imageRef.current?.removeEventListener('load', updateImageWidth);
        };
      }
    }
  }, [success]); // Re-run when image changes

  // Function to randomly select a message
  const getRandomMessage = (): MessageConfig => {
    const randomIndex = Math.floor(Math.random() * MESSAGES.length);
    return MESSAGES[randomIndex];
  };

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitSeconds !== null && rateLimitSeconds > 0) {
      const timer = setTimeout(() => {
        setRateLimitSeconds(rateLimitSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (rateLimitSeconds === 0) {
      setRateLimitSeconds(null);
      setError(null);
    }
  }, [rateLimitSeconds]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const handleSendMessage = async (): Promise<void> => {
    // Track button tap event in Mixpanel
    if (typeof window !== 'undefined' && (window as any).mixpanel) {
      (window as any).mixpanel.track('be_nice_button_tapped');
    }

    // Reset previous states
    setError(null);
    setSuccess(null);
    setSuccessMessage(null);
    setLoading(true);

    // Randomly select a message
    const selectedMessage = getRandomMessage();

    // Prepare request body with message and optional image
    const requestBody: {
      message?: string;
      image?: PredefinedImageName;
    } = {};
    
    if (selectedMessage.text) {
      requestBody.message = selectedMessage.text;
    }
    
    if (selectedMessage.image) {
      requestBody.image = selectedMessage.image;
    }

    try {
      const response = await fetch('/api/send-message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response is OK before trying to parse JSON
      if (!response.ok) {
        // Try to parse error response
        let errorData: ApiResponse;
        try {
          errorData = await response.json();
        } catch (parseError) {
          // If response isn't JSON, it's likely the API server isn't running
          if (response.status === 404) {
            setError('API endpoint not found. Make sure the API server is running on port 3001.');
          } else {
            setError(`Server error (${response.status}). Please check that the API server is running.`);
          }
          return;
        }

        // Handle error responses
        if (response.status === 429) {
          const rateLimitError = errorData as RateLimitErrorResponse;
          const retryAfter = rateLimitError.retryAfter || 3600;
          setRateLimitSeconds(retryAfter);
          setError(`Rate limit exceeded. Please try again in ${formatTime(retryAfter)}.`);
        } else if (response.status === 500 || response.status === 503) {
          const deliveryError = errorData as DeliveryErrorResponse | ServiceUnavailableErrorResponse;
          setError(deliveryError.details || deliveryError.error || 'SMS delivery failed. Please try again later.');
        } else {
          const errorMsg = (errorData as any)?.error || (errorData as any)?.details || `Unexpected error (${response.status})`;
          setError(errorMsg);
        }
        return;
      }

      // Parse success response
      const responseData = await response.json() as SendMessageSuccessResponse;

      // Set success state and message based on whether message was scheduled or sent immediately
      setSuccess('sent');
      if (responseData.scheduled && responseData.scheduledForDisplay) {
        // Message was scheduled
        setSuccessMessage('Thanks! Dudley will be a lot nicer because of you. 🩵');
      } else {
        // Message was sent immediately
        setSuccessMessage('Thanks! Dudley is a lot nicer because of you. 🩵');
      }

    } catch (error) {
      // Network or other errors
      console.error('Error sending message:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        setError('Unable to connect to API. Make sure the API server is running on port 3001. Run: pnpm run dev:vercel -- --listen 3001');
      } else if (error instanceof SyntaxError) {
        setError('Invalid response from server. Please check that the API server is running.');
      } else {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        setError(`An unexpected error occurred: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a 
        href="#main-content" 
        className="sr-only"
      >
        Skip to main content
      </a>
      
      <div className="min-h-screen flex flex-col items-center justify-center p-8 font-sans bg-gray-900 relative overflow-hidden">
        {/* Animated background pattern */}
        <div 
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            animation: 'drift 20s linear infinite',
          }}
          aria-hidden="true"
        />

        <main 
          id="main-content"
          className="max-w-md w-full text-center relative z-10 animate-slide-up" 
          role="main"
          aria-labelledby="page-heading"
        >
          {/* ARIA live region for status announcements */}
          <div 
            aria-live="polite" 
            aria-atomic="true" 
            className="sr-only"
            id="status-announcements"
          >
            {loading && 'Sending message, please wait.'}
            {successMessage && successMessage}
            {error && error}
            {rateLimitSeconds !== null && rateLimitSeconds > 0 && `Rate limit active. Try again in ${formatTime(rateLimitSeconds)}.`}
          </div>

          {/* Page heading - visually hidden but available to screen readers */}
          <h1 id="page-heading" className="sr-only">
            Dudley Be Nice - Send Friendly Reminders
          </h1>

          {/* Image - show mean.png initially, nice.png after success */}
          <div className="mb-6 flex justify-center">
            <img 
              ref={imageRef}
              src={success ? niceImage : meanImage} 
              alt={success 
                ? "A friendly image showing Dudley being nice" 
                : "An image of Dudley with a reminder to be nice"} 
              className="max-w-full h-auto max-h-80 object-contain rounded-lg animate-fade-in"
              role="img"
            />
          </div>

          {/* Button - only show when not successful (immediate send) */}
          {!success && (
            <div 
              className="mx-auto"
              style={{ 
                width: imageWidth ? `${imageWidth}px` : '100%',
                maxWidth: '100%'
              }}
            >
              <button
                onClick={handleSendMessage}
                disabled={loading || rateLimitSeconds !== null}
                className="w-full py-4 px-8 text-lg font-semibold rounded-xl cursor-pointer transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 relative z-10"
                style={{ 
                  color: 'white',
                  background: 'linear-gradient(to right, #667eea, #764ba2)',
                  border: 'none'
                }}
                aria-label={loading ? 'Sending message, please wait' : 'Send a be nice reminder to Dudley'}
                aria-busy={loading}
                aria-disabled={loading || rateLimitSeconds !== null}
              >
              {loading && (
                <svg 
                  className="animate-spin-slow h-5 w-5 text-white" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  role="img"
                >
                  <title>Loading spinner</title>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <span style={{ color: 'white' }}>{loading ? 'Sending...' : 'Be Nice!'}</span>
              </button>
            </div>
          )}

          {rateLimitSeconds !== null && rateLimitSeconds > 0 && !success && (
            <div 
              className="mt-4 p-3 bg-amber-50 text-amber-900 rounded-lg text-sm border border-amber-200"
              role="alert"
              aria-live="polite"
            >
              <span aria-label="Rate limit active">⏱️</span> Try again in <span className="font-semibold text-amber-700" aria-label={`${formatTime(rateLimitSeconds)} remaining`}>{formatTime(rateLimitSeconds)}</span>
            </div>
          )}

          {successMessage && success && (
            <div 
              className="mt-6 text-gray-200 text-base leading-relaxed animate-fade-in"
              role="status"
              aria-live="polite"
            >
              {successMessage}
            </div>
          )}

          {error && rateLimitSeconds === null && !success && (
            <div 
              className="mt-6 p-4 bg-gradient-to-r from-red-50 to-red-100 text-red-800 rounded-xl text-sm leading-relaxed border border-red-200 animate-fade-in"
              role="alert"
              aria-live="assertive"
            >
              {error}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default App;
